/* eslint-disable camelcase */
import { DATABASE_TYPES, DAYS, VALIDATION } from 'constant';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { actions } from 'redux/actions';
import { ACTIONS_TYPES } from 'redux/helper';
import Shared from 'screens/shared';
import { formField } from 'types';
import {
  _t, getKey, isSsdStorageType, toInt,
} from 'utils';
import { getResourceLimitsForContract } from 'utils/contract';

export default ({
  values,
}) => {
  const normalizeProperties = (props) => {
    const { properties, ...rest } = props;
    return {
      properties: {
        ...properties,
        ram: parseInt(properties?.ram, 10) / 1024,
        storageSize: parseInt(properties?.storageSize, 10) / 1024,
      },
      ...rest,
    };
  };

  const {
    [ACTIONS_TYPES.DATABASE.POSTGRESQL.GET_UPGRADABLE_VERSIONS]: versions = [],
    [ACTIONS_TYPES.CONTRACT.GET_ALL]: contracts = [],
    [ACTIONS_TYPES.DATA_CENTER.GET_ALL]: dataCenters = [],
    [ACTIONS_TYPES.DATABASE.GET_QUOTA]: dbQuota = {},
  } = useSelector((e) => e);
  const [database, setDatabase] = useState(normalizeProperties(values));

  useEffect(() => {
    setDatabase(normalizeProperties(values));
  }, [values]);

  const { id: clusterId, properties } = database;

  const checkQuotas = (formValues) => {
    const cluster = formValues || properties;
    const { instances } = cluster;
    const { quotaUsage = {}, quotaLimits = {} } = dbQuota || {};

    let maxCoresValue = toInt(quotaLimits?.cpu, 8) - toInt(quotaUsage?.cpu) - ((cluster.cores * instances) || 0)
      + ((values.properties.cores) * values.properties.instances);
    let maxRamValue = toInt(quotaLimits?.memory, 16) - toInt(quotaUsage?.memory) - ((cluster.ram * instances) || 0)
      + ((values.properties.ram / 1024) * values.properties.instances);
    const maxStorageValue = (toInt(quotaLimits?.storage, 150) - toInt(quotaUsage?.storage))
      + ((values.properties.storageSize / 1024) * values.properties.instances);

    const resourceLimits = getResourceLimitsForContract(contracts);

    const {
      coresPerContract = maxCoresValue,
      ramPerContract = (maxRamValue * 1024),
    } = resourceLimits;
    const maxContractRam = ramPerContract / 1024;
    if (coresPerContract < maxCoresValue) {
      maxCoresValue = coresPerContract;
    }
    if (maxContractRam < maxRamValue) {
      maxRamValue = maxContractRam;
    }

    const quotaValues = {
      maxCores: maxCoresValue,
      maxRam: maxRamValue,
      maxStorage: maxStorageValue,
    };

    return {
      ...quotaValues,
    };
  };

  const { maxCores, maxRam, maxStorage } = checkQuotas();

  const getDataCenter = (dcId) => (dataCenters.find(({ id }) => id === dcId) || {});
  const getDataCenterName = (dcId) => {
    const { properties: dcProps = {} } = getDataCenter(dcId);
    return dcProps.name;
  };

  const maxStorageLimit = Math.floor(maxStorage / properties.instances);

  const fields = [
    formField.text({ name: 'displayName', title: _t('DATABASES.FORM_FIELDS.DISPLAY_NAME.TITLE') }),
    formField.dropdown({ name: 'postgresVersion', title: _t('DATABASES.FORM_FIELDS.POSTGRES_VERSION.TITLE') }),
    formField.number({
      name: 'instances', title: _t('DATABASES.FORM_FIELDS.INSTANCES.TITLE'),
    }),
    formField.slider({
      name: 'cores',
      title: _t('DATABASES.FORM_FIELDS.CORES.TITLE'),
      showValue: true,
      instantChange: false,
      range: [1, maxCores > 8 ? maxCores : 8],
      options: [4, 8].filter((item) => item <= maxCores),
    }),
    formField.slider({
      name: 'ram',
      title: _t('DATABASES.FORM_FIELDS.RAM.TITLE'),
      showValue: true,
      instantChange: false,
      range: [2, maxRam > 16 ? maxRam : 16],
      options: [4, 8, 16].filter((item) => item <= maxRam),
    }),
    formField.number({
      name: 'storageSize',
      title: _t('DATABASES.FORM_FIELDS.STORAGE_SIZE.TITLE'),
      min: 1,
      instantChange: false,
      stepIsMandatory: true,
      step: '1',
      suffix: 'GB',
      helpText: isSsdStorageType(values.properties.storageType) ? _t('DATABASES.FORM_FIELDS.STORAGE_SIZE.SSD_INFO') : '',
    }),
    formField.object({
      name: 'maintenanceWindow',
      title: _t('DATABASES.FORM_FIELDS.MAINTENANCE_WINDOW.TITLE'),
      subTitle: _t('DATABASES.FORM_FIELDS.MAINTENANCE_WINDOW.SUBTITLE'),
      fields: [
        formField.time({ name: 'time', title: _t('Maintenance time') }),
        formField.dropdown({ name: 'dayOfTheWeek', title: _t('Maintenance day'), options: DAYS }),
      ],
    }),
    formField.array({
      name: 'connections',
      type: 'array',
      icon: 'boxes',
      title: _t('DATABASES.FORM_FIELDS.CONNECTIONS.TITLE'),
      // subTitle: 'Multiple connections can be added', // currently only 1 connection supported
      actionButtonName: _t('DATABASES.FORM_FIELDS.CONNECTIONS.ACTION_BUTTON'),
      maxItems: 1,
      setItemTitle: (con) => getDataCenterName(con.datacenterId),
      setItemDetails: (con) => `LAN: ${con.lanId}${con.cidr ? `, IP: ${con.cidr}` : ''}`,
      updateProps: (props) => {
        const { location } = props;
        const _fields = fields.find((e) => e.name === 'connections').fields;
        _fields.find((e) => e.name === 'datacenterId').location = location;
        return { fields: _fields };
      },
      onEdit: ({ datacenterId }) => {
        actions.common.quickUpdate({ [ACTIONS_TYPES.DATA_CENTER.GET]: getDataCenter(datacenterId) });
      },
      fields: [
        formField.datacenter({
          name: 'datacenterId',
          type: 'datacenter',
          external: true,
          title: _t('DATABASES.FORM_FIELDS.CONNECTIONS.FIELDS.DATACENTER.TITLE'),
          setValueFrom: (e) => {
            actions.common.quickUpdate({ [ACTIONS_TYPES.DATA_CENTER.GET]: e });
            return e.id;
          },
        }),
        formField.lan({
          name: 'lanId',
          type: 'lan',
          hasNewOption: false,
          external: true,
          privateOnly: true,
          title: _t('DATABASES.FORM_FIELDS.CONNECTIONS.FIELDS.LAN_ID.TITLE'),
          updateProps: (con) => ({ disabled: !con.datacenterId }),
        }),
        formField.text({
          name: 'cidr',
          type: 'text',
          title: _t('DATABASES.FORM_FIELDS.CONNECTIONS.FIELDS.PRIVATE_IP.TITLE'),
          helpText: (
            <span>
              {_t('DATABASES.FORM_FIELDS.CONNECTIONS.FIELDS.PRIVATE_IP.HELP_TEXT.TEXT')}
              &nbsp;
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://docs.ionos.com/cloud/managed-services/database-as-a-service/postgresql/dcd-howtos/setup-a-cluster-in-the-dcd/"
              >
                {_t('DATABASES.FORM_FIELDS.CONNECTIONS.FIELDS.PRIVATE_IP.HELP_TEXT.HREF')}
              </a>
            </span>
          ),
        }),
      ],
      validations: [
        { name: 'datacenterId', type: VALIDATION.REQUIRED },
        { name: 'lanId', type: VALIDATION.REQUIRED },
        {
          name: 'lanId', type: VALIDATION.NUMBER, min: 1, max: 50,
        },
        { name: 'cidr', type: VALIDATION.REQUIRED },
        { name: 'cidr', type: VALIDATION.PRIVATE_IP_SUBNET },
      ],
    }),
  ];

  const validations = [
    { name: 'displayName', type: VALIDATION.REQUIRED },
    { name: 'displayName', type: VALIDATION.STRING, max: 256 },

    { name: 'postgresVersion', type: VALIDATION.REQUIRED },
    { name: 'instances', type: VALIDATION.REQUIRED },
    {
      name: 'instances', type: VALIDATION.NUMBER, min: 1, max: 5,
    },
    {
      name: 'ram', type: VALIDATION.NUMBER, min: 2, max: 64,
    },
    {
      name: 'cores', type: VALIDATION.NUMBER, min: 1, max: 16,
    },
    {
      name: 'storageSize', type: VALIDATION.DECIMAL, min: 2, max: 2048,
    },
  ];

  useEffect(() => {
    actions.dataCenter.getAll();
    if (Object.keys(dbQuota).length === 0) {
      actions.database.getQuota();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (clusterId) actions.database.postgresql.getUpgradableVersions({ clusterId });
  }, [clusterId]);

  useEffect(() => {
    if (contracts.length === 0) {
      actions.contract.getAll();
    }
  }, [contracts.length]);

  const changeValues = (input) => {
    const output = input;

    output.ram *= 1024;
    output.storageSize *= 1024;

    return output;
  };

  const submitHandler = (props) => {
    const changedProps = changeValues(props);
    actions.database.postgresql.update({ id: clusterId, properties: changedProps }).then((result) => {
      if (getKey('isAccessByDCD') && !result.httpStatus) {
        setTimeout(() => {
          actions.database.postgresql.get({ id: clusterId });
        }, 2000);
      }
    });
  };

  const postgresVersions = versions.map((item) => item.name);

  const ramOptions = [4, 8, 16].filter((e) => e < maxRam);
  const coreOptions = [4, 8].filter((e) => e < maxCores);

  return (
    <Shared.forms.Form
      onChange={(value) => {
        setDatabase({ ...database, properties: { ...value } });
      }}
      fields={fields}
      fieldsValues={{
        postgresVersion: properties?.postgresVersion,
        displayName: properties?.displayName,
        maintenanceWindow: properties?.maintenanceWindow,
        cores: properties?.cores,
        ram: parseInt(properties?.ram, 10),
        storageSize: parseInt(properties?.storageSize, 10),
        instances: properties?.instances,
        connections: properties?.connections,
      }}
      onSubmit={submitHandler}
      validations={validations}
      changeFields={[
        { name: 'postgresVersion', props: { options: postgresVersions } },
        {
          name: 'storageSize',
          props: {
            element: (
              <div className="flex flex-row flex-justify-end">
                <div className="m-r-15">
                  <Shared.billing.DBaaSPriceEstimation
                    title={_t('COMMON.LABELS.CURRENT_ESTIMATED_COST')}
                    model={values && normalizeProperties(values).properties}
                    databaseType={DATABASE_TYPES.POSTGRES}
                    showInfo={false}
                  />
                </div>
                <div>
                  <Shared.billing.DBaaSPriceEstimation
                    title={_t('COMMON.LABELS.NEW_ESTIMATED_COST')}
                    model={properties}
                    databaseType={DATABASE_TYPES.POSTGRES}
                  />
                </div>
              </div>
            ),
          },
        },
        ...(
          maxCores < 0
            ? [{ name: 'cores', props: { error: 'You already consumed the maximum limit', options: coreOptions } }]
            : [{
              name: 'cores',
              props: { error: '', range: [1, maxCores > 8 ? maxCores : 8], options: coreOptions },
            }]
        ),
        ...(
          maxRam < 0
            ? [{ name: 'ram', props: { error: 'You already consumed the maximum limit', options: ramOptions } }]
            : [{
              name: 'ram',
              props: { error: '', range: [2, maxRam > 16 ? maxRam : 16], options: ramOptions },
            }]
        ),
        ...(
          maxStorage - (properties.storageSize * properties.instances) < 0
            ? [{ name: 'storageSize', props: { error: `You already consumed the maximum limit. You have only ${maxStorageLimit} GB left.` } }]
            : [{ name: 'storageSize', props: { error: '' } }]
        ),
      ]}
      isCancel={false}
      validateOnBlur
    />
  );
};
